import React, {useEffect, useState} from 'react';
import {Platform, StyleSheet, Text, View} from 'react-native';
import DeviceInfo from 'react-native-device-info';
import {GestureHandlerRootView} from 'react-native-gesture-handler'; // required by https://reactnavigation.org/docs/drawer-navigator/#installation
import {SafeAreaView} from 'react-native-safe-area-context';
import {
  createDrawerNavigator,
  DrawerContentComponentProps,
  DrawerContentScrollView,
  DrawerItem,
} from '@react-navigation/drawer';
import {useNavigation} from '@react-navigation/native';
import {observer} from 'mobx-react-lite';

import {Icon} from '../components/Icon';
import {fontFamily} from '../components/ui-controls/Text';
import config from '../config';
import {space} from '../design-system/tokens';
import {handleLogoutPress} from '../helpers/authHelpers';
import {AppRoute} from '../models/AppRoute';
import type {DrawerNavigatorScreenProps, DrawerParamList} from '../models/navigationTypes';
import SplashScreen from '../screens/SplashScreen';
import {useStores} from '../stores/use-stores';
import theme from '../style/theme';
import {navigateToLiquorShop} from '../utils/giftCards';

import {StoreType} from './routes/types';
import {
  drawerEntries,
  GO_BACK_TO_STORE_LABEL,
  LOG_OUT_LABEL,
  showGoBackToStore,
} from './drawerEntries';
import BottomTabNavigator from './TabNavigator';
import {shouldDetachInactiveScreens} from './util';

const LogoutIcon = ({color}: {color: string}) => (
  <Icon
    name="arrow-right-from-bracket"
    size={20}
    color={color}
    style={{
      marginLeft: 8,
    }}
  />
);

const GoBackToStoreDrawerItem = () => {
  const rootStore = useStores();
  // Use root navigation to access ROOT_STORE_SELECTION_FLOW
  const navigation = useNavigation();
  const handleNavigateToLiquorShop = () => navigateToLiquorShop(navigation, rootStore);
  return (
    <DrawerItem
      label={GO_BACK_TO_STORE_LABEL}
      onPress={handleNavigateToLiquorShop}
      activeTintColor={theme.colour.text.drawer.primary}
      inactiveTintColor={theme.colour.text.drawer.primary}
      icon={LogoutIcon}
      labelStyle={{fontFamily}}
    />
  );
};

type LogoutDrawerItemProps = {
  isLoggedIn: boolean;
  onLogoutPress: () => void;
};

const LogoutDrawerItem = ({onLogoutPress, isLoggedIn}: LogoutDrawerItemProps) =>
  isLoggedIn ? (
    <DrawerItem
      label={LOG_OUT_LABEL}
      onPress={onLogoutPress}
      activeTintColor={theme.colour.text.drawer.primary}
      inactiveTintColor={theme.colour.text.drawer.primary}
      icon={LogoutIcon}
      labelStyle={{fontFamily}}
    />
  ) : null;

const VERSION_FONT_SIZE = 12;

const VersionDisplay = () => {
  const [version, setVersion] = useState<string>('');

  useEffect(() => {
    const getAppVersion = async () => {
      if (Platform.OS === 'web') {
        // For web, use APP_VERSION injected by webpack DefinePlugin
        const webVersion = process.env.APP_VERSION || '0.0.0';
        setVersion(webVersion);
      } else {
        // For native platforms, use DeviceInfo
        const nativeVersion = await DeviceInfo.getVersion();
        setVersion(nativeVersion);
      }
    };

    getAppVersion();
  }, []);

  if (!version) {
    return null;
  }

  const envLabel = config.ENV ? ` (${config.ENV})` : '';

  return (
    <View
      style={styles.versionContainer}
      accessible
      accessibilityLabel={`Version ${version}${envLabel}`}>
      <Icon
        name="circle-info"
        size={VERSION_FONT_SIZE}
        color={theme.colour.text.drawer.primary}
        style={styles.versionIcon}
      />
      <Text style={styles.versionText}>
        {version}
        {envLabel}
      </Text>
    </View>
  );
};

type CustomDrawerContentProps = DrawerContentComponentProps &
  LogoutDrawerItemProps & {
    storeType: StoreType;
    storeLoyaltyEnabled: boolean;
    ecommerceDisabled?: boolean;
  };
const CustomDrawerContent = (props: CustomDrawerContentProps) => (
  <DrawerContentScrollView
    {...props}
    testID="drawer-content"
    contentContainerStyle={{
      flex: 1,
      flexDirection: 'column',
      justifyContent: 'space-between',
    }}>
    <View>
      {drawerEntries({
        storeType: props.storeType,
        storeLoyaltyEnabled: props.storeLoyaltyEnabled,
        ecommerceDisabled: props.ecommerceDisabled,
        isLoggedIn: props.isLoggedIn,
      }).map(({name, title, params, icon}) => {
        // DrawerContentComponentProps & DrawerNavigationHelpers should support ParamList typing #9348
        // https://github.com/react-navigation/react-navigation/issues/9348
        const handleDrawerItemPress = () => {
          // React Navigation v7 requires nested navigation syntax
          // All drawer routes are nested inside MAIN navigator
          // @ts-expect-error - Type mismatch needs investigation
          props.navigation.navigate(AppRoute.MAIN, {
            screen: name,
            params: params,
          });
        };
        return (
          <DrawerItem
            key={name}
            testID={`drawer-item-${name}`}
            label={title || ''}
            onPress={handleDrawerItemPress}
            icon={icon}
            activeTintColor={theme.colour.text.drawer.primary}
            inactiveTintColor={theme.colour.text.drawer.primary}
            labelStyle={{fontFamily}}
          />
        );
      })}
    </View>
    <View>
      {showGoBackToStore(props.storeType) && <GoBackToStoreDrawerItem />}
      <LogoutDrawerItem
        key={'logout'}
        isLoggedIn={props.isLoggedIn}
        onLogoutPress={props.onLogoutPress}
      />
      <VersionDisplay />
    </View>
  </DrawerContentScrollView>
);

const Drawer = createDrawerNavigator<DrawerParamList>();

const DrawerNavigator = observer(({navigation}: DrawerNavigatorScreenProps) => {
  const {globalStore, userStore, shopSelectionStore} = useStores();
  const isLoggedIn = userStore.isLoggedIn;

  const storeType = shopSelectionStore?.selectedStoreType;

  let bottomStyle;
  if (globalStore.safeAreaColour) {
    bottomStyle = [styles.bottomMain, {backgroundColor: globalStore.safeAreaColour}];
  } else {
    bottomStyle = styles.bottomMain;
  }

  // On Android with edge-to-edge, the tab bar owns the bottom inset
  // (via useEdgeToEdgeBottomInset). Including 'bottom' here would double-apply it.
  const safeAreaEdges =
    Platform.OS === 'android'
      ? (['left', 'right'] as const)
      : (['bottom', 'left', 'right'] as const);

  // This didn't work on App.tsx and I ran out of time so doing it here
  if (globalStore.isUserDataInitialising) {
    return <SplashScreen />;
  }

  return (
    <GestureHandlerRootView style={{flex: 1}} testID="gesture-handler-root-view">
      <SafeAreaView style={bottomStyle} edges={safeAreaEdges}>
        <Drawer.Navigator
          // DIS-1420: see shouldDetachInactiveScreens — no native Screen detach (Fabric reparent crash).
          detachInactiveScreens={shouldDetachInactiveScreens()}
          screenOptions={{
            headerShown: false,
            drawerStyle: styles.main,
            drawerActiveBackgroundColor: theme.colour.background.drawer,
            drawerActiveTintColor: 'white',
            drawerInactiveTintColor: 'white',
            swipeEnabled: false,
            swipeMinDistance: 0,
          }}
          drawerContent={props => (
            <CustomDrawerContent
              onLogoutPress={() => handleLogoutPress(navigation, userStore)}
              isLoggedIn={isLoggedIn}
              storeType={storeType}
              storeLoyaltyEnabled={shopSelectionStore.loyaltyEnabled}
              ecommerceDisabled={shopSelectionStore.ecommerceDisabled}
              {...props}
            />
          )}>
          <Drawer.Screen
            name={AppRoute.MAIN}
            component={BottomTabNavigator}
            options={{
              drawerLabelStyle: styles.drawerScreenItemLabelStyle,
            }}
          />
        </Drawer.Navigator>
      </SafeAreaView>
    </GestureHandlerRootView>
  );
});

export default DrawerNavigator;

const styles = StyleSheet.create({
  main: {
    backgroundColor: theme.colour.background.drawer,
    flex: 1,
  },
  topMain: {
    flex: 0,
    backgroundColor: theme.colour.background.drawer,
  },
  bottomMain: {
    flex: 1,
    backgroundColor: 'white',
  },
  drawerScreenItemLabelStyle: {
    color: theme.colour.text.drawer.primary,
    fontFamily,
  },
  versionContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: space.s4,
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: 'center',
  },
  versionIcon: {opacity: 0.7},
  versionText: {
    color: theme.colour.text.drawer.primary,
    fontFamily,
    fontSize: VERSION_FONT_SIZE,
    opacity: 0.7,
  },
});
